import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Plus, Eye, Trash2, Edit, Search, X, ChevronDown, ChevronUp, Printer } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/lib/supabase";
import { fetchDocuments, deleteDocument } from '@/lib/api/documents';
import { DocumentForm } from './DocumentForm';
import { useAuth } from '@/components/auth/AuthContext';
import { ProductSelector } from "@/components/ProductSelector";
import { useColorSettings } from '@/contexts/ColorSettingsContext';
import { fetchSeasonDatePresets } from "@/lib/seasonDatePresets";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const DELIVERY_DOCUMENT_ID_BATCH_SIZE = 75;

const chunkArray = (items, size) => {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

export function DocumentList({ type }) {
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [seasonDatePresets, setSeasonDatePresets] = useState([]);
  const { toast } = useToast();
  const { hasPermission } = useAuth();
  const { colorSettings } = useColorSettings();

  useEffect(() => {
    checkUserRole();
    loadDocuments();
    loadSeasonDatePresets();
  }, [type]);

  useEffect(() => {
    // Actualizar documentos filtrados combinando filtros activos
    let list = documents;

    if (selectedProduct) {
      list = list.filter(doc => doc.items?.some(item => item.product_id === selectedProduct));
    }

    if (type === 'salida') {
      const term = customerSearchTerm.trim().toLowerCase();
      if (term) {
        list = list.filter(doc => (doc.customer?.name || '').toLowerCase().includes(term));
      }
    }

    if (dateRange.from) {
      list = list.filter(doc => String(doc.date || "").slice(0, 10) >= dateRange.from);
    }

    if (dateRange.to) {
      list = list.filter(doc => String(doc.date || "").slice(0, 10) <= dateRange.to);
    }

    setFilteredDocuments(list);
  }, [documents, selectedProduct, customerSearchTerm, dateRange, type]);

  const checkUserRole = async () => {
    const isAdminUser = hasPermission("salida");
    setIsAdmin(isAdminUser);
  };

  const loadDocuments = async () => {
    try {
      let data = await fetchDocuments(type);
      
      // Para documentos de salida, obtener información adicional de entregas
      if (type === 'salida' && data.length > 0) {
        // PostgREST serializa `.in()` en la URL; con muchos documentos la petición puede superar el límite.
        const documentIdBatches = chunkArray(
          data.map(doc => doc.id),
          DELIVERY_DOCUMENT_ID_BATCH_SIZE
        );
        const allDeliveries = [];

        for (const documentIds of documentIdBatches) {
          const { data: deliveriesBatch, error: deliveryError } = await supabase
            .from('order_deliveries')
            .select(`
              id,
              delivery_number,
              document_id,
              order:orders(number)
            `)
            .in('document_id', documentIds);

          if (deliveryError) {
            console.error('Error fetching deliveries:', deliveryError);
            break;
          }

          allDeliveries.push(...(deliveriesBatch || []));
        }

        // Crear mapa para acceso rápido O(1) en lugar de búsqueda O(n)
        const deliveryMap = new Map();
        allDeliveries.forEach(delivery => {
          deliveryMap.set(delivery.document_id, delivery);
        });

        // Asignar entregas a documentos usando el mapa
        data = data.map(doc => ({
          ...doc,
          delivery: deliveryMap.get(doc.id) || null
        }));
      }

      // Ordenar documentos de forma descendente por número
      const sortedData = data.sort((a, b) => {
        // Convertir a números si es posible, sino ordenar alfabéticamente
        const numA = parseInt(a.number) || 0;
        const numB = parseInt(b.number) || 0;
        if (numA !== 0 && numB !== 0) {
          return numB - numA; // Orden descendente
        }
        // Si no son números, ordenar alfabéticamente de forma descendente
        return b.number.localeCompare(a.number);
      });
      
      setDocuments(sortedData);
      setFilteredDocuments(sortedData);
    } catch (error) {
      console.error('Error loading documents:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los documentos",
        variant: "destructive"
      });
    }
  };

  const loadSeasonDatePresets = async () => {
    const presets = await fetchSeasonDatePresets();
    setSeasonDatePresets((presets || []).filter((p) => p.active !== false));
  };

  const handleDelete = async (document) => {
    setDocumentToDelete(document);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (type === 'salida') {
        // Para documentos de salida, usar borrado inteligente con recuperación
        await deleteDocumentWithRecovery(documentToDelete.id);
        toast({
          title: "Éxito",
          description: "Documento de salida eliminado y cantidades recuperadas correctamente"
        });
      } else {
        // Para documentos de entrada, borrado normal
        await deleteDocument(documentToDelete.id);
        toast({
          title: "Éxito",
          description: "Documento eliminado correctamente"
        });
      }
      
      await loadDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo eliminar el documento",
        variant: "destructive"
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setDocumentToDelete(null);
    }
  };

  const deleteDocumentWithRecovery = deleteDocument;

  const handleEdit = (document) => {
    window.location.hash = `#/${type}/${document.id}`;
  };

  const handlePrint = async (document) => {
    if (type !== 'salida') {
      toast({
        title: "Error",
        description: "Solo se pueden imprimir documentos de salida",
        variant: "destructive"
      });
      return;
    }

    // Cargar las imágenes de los productos solo cuando se necesita imprimir
    let documentWithImages = { ...document };
    
    if (document.items && document.items.length > 0) {
      try {
        // Obtener los IDs de productos únicos
        const productIds = [...new Set(document.items.map(item => item.product_id))];
        
        // Cargar productos con imágenes
        const { data: productsWithImages, error } = await supabase
          .from('products')
          .select('id, code, description, images, main_image_index')
          .in('id', productIds);

        if (error) {
          console.error('Error loading product images:', error);
        } else {
          // Crear mapa de productos con imágenes
          const productsMap = new Map();
          productsWithImages?.forEach(product => {
            productsMap.set(product.id, product);
          });

          // Actualizar items con imágenes
          documentWithImages = {
            ...document,
            items: document.items.map(item => ({
              ...item,
              product: {
                ...item.product,
                images: productsMap.get(item.product_id)?.images || [],
                main_image_index: productsMap.get(item.product_id)?.main_image_index || 0
              }
            }))
          };
        }
      } catch (error) {
        console.error('Error loading images for print:', error);
        // Continuar con el documento original si falla la carga de imágenes
        documentWithImages = document;
      }
    }

    // Agrupar items por código de producto y color
    const groupedItems = documentWithImages.items.reduce((acc, item) => {
      const key = `${item.product_id}-${item.color}`;
      if (!acc[key]) {
        acc[key] = {
          product_id: item.product_id,
          product_code: item.product?.code || 'N/A',
          description: item.product?.description || 'Sin descripción',
          color: item.color,
          sizes: {},
          total_quantity: 0,
          total_amount: 0,
          price: item.price,
          product: item.product
        };
      }
      acc[key].sizes[item.size] = item.quantity;
      acc[key].total_quantity += item.quantity;
      acc[key].total_amount += item.quantity * item.price;
      return acc;
    }, {});

    // Obtener todas las tallas disponibles para ordenarlas
    const allSizes = ["P", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL"];

    // Función auxiliar para obtener el color hexadecimal usando el contexto
    const getColorHex = (colorName) => {
      if (!colorName) return '#C0C0C0';
      
      // Primero intentar usar el contexto de colores
      if (colorSettings && colorSettings[colorName]) {
        const colorData = colorSettings[colorName];
        const hexColor = typeof colorData === 'string' ? colorData : colorData.hex;
        
        return hexColor || '#C0C0C0';
      }
      
      // Fallback: mapeo manual de colores comunes
      const colorMap = {
        'negro': '#000000',
        'blanco': '#FFFFFF',
        'rojo': '#FF0000',
        'azul': '#0000FF',
        'verde': '#008000',
        'amarillo': '#FFFF00',
        'gris': '#808080',
        'marron': '#8B4513',
        'rosa': '#FFC0CB',
        'morado': '#800080',
        'naranja': '#FFA500',
        'kaki': '#C3B091',
        'beige': '#F5F5DC',
        'crema': '#FFFDD0',
        'azul marino': '#000080',
        'verde oliva': '#808000',
        'rojo oscuro': '#8B0000',
        'azul claro': '#87CEEB',
        'verde claro': '#90EE90',
        'gris claro': '#D3D3D3',
        'gris oscuro': '#404040',
        // Variaciones comunes
        'caqui': '#C3B091',
        'khaki': '#C3B091',
        'café': '#8B4513',
        'cafe': '#8B4513',
        'brown': '#8B4513',
        'black': '#000000',
        'white': '#FFFFFF',
        'red': '#FF0000',
        'blue': '#0000FF',
        'green': '#008000',
        'yellow': '#FFFF00',
        'gray': '#808080',
        'grey': '#808080',
        'pink': '#FFC0CB',
        'purple': '#800080',
        'orange': '#FFA500'
      };
      
      const normalizedColor = colorName.toLowerCase().trim();
      const hexColor = colorMap[normalizedColor];
      
      return hexColor || '#C0C0C0';
    };

    // Función auxiliar para obtener la descripción del color
    const getColorDescription = (colorName) => {
      if (!colorName) return 'Sin color';
      
      // Usar el contexto de colores si está disponible
      if (colorSettings && colorSettings[colorName]) {
        const colorData = colorSettings[colorName];
        const description = typeof colorData === 'string' ? colorName : (colorData.description || colorName);
        return description;
      }
      
      // Fallback: usar el nombre del color tal como viene
      return colorName;
    };

    const printWindow = window.open('', '_blank');
    if (!printWindow || printWindow.closed) {
      toast({
        title: "Error",
        description: "No se pudo abrir la ventana de impresión (posible bloqueador de pop‑ups)",
        variant: "destructive"
      });
      return;
    }
    const printContent = `
      <html>
        <head>
          <title>Salida de Almacén #${document.number}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
            
            * { box-sizing: border-box; }
            
            body { 
              font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; 
              margin: 0; 
              padding: 20px; 
              background: #f8fafc;
              color: #1e293b;
              line-height: 1.6;
            }
            
            .print-container {
              max-width: 1400px;
              margin: 0 auto;
              background: white;
              border-radius: 12px;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
              overflow: hidden;
            }
            
            .header {
              background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
              color: white;
              padding: 30px;
              text-align: center;
            }
            
            .header h1 {
              margin: 0 0 10px 0;
              font-size: 2.5rem;
              font-weight: 700;
              letter-spacing: -0.025em;
            }
            
            .header-info {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: 20px;
              margin-top: 20px;
            }
            
            .header-item {
              background: rgba(255, 255, 255, 0.1);
              padding: 15px;
              border-radius: 8px;
              backdrop-filter: blur(10px);
            }
            
            .header-item h3 {
              margin: 0 0 5px 0;
              font-size: 0.875rem;
              font-weight: 500;
              opacity: 0.9;
              text-transform: uppercase;
              letter-spacing: 0.05em;
            }
            
            .header-item p {
              margin: 0;
              font-size: 1.125rem;
              font-weight: 600;
            }
            
            .content {
              padding: 30px;
            }
            
            .product-card {
              background: white;
              border: 1px solid #e2e8f0;
              border-radius: 12px;
              margin-bottom: 30px;
              overflow: hidden;
              box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
            }
            
            .product-header {
              background: #f8fafc;
              padding: 25px;
              border-bottom: 1px solid #e2e8f0;
              display: flex;
              align-items: center;
              gap: 25px;
            }
            
            .product-image {
              width: 120px;
              height: 120px;
              object-fit: cover;
              border-radius: 8px;
              border: 2px solid #e2e8f0;
            }
            
            .product-info {
              flex: 1;
            }
            
            .product-code {
              font-size: 2rem;
              font-weight: 700;
              color: #1e293b;
              margin: 0 0 10px 0;
              letter-spacing: -0.025em;
            }
            
            .product-description {
              font-size: 1.25rem;
              color: #475569;
              margin: 0 0 15px 0;
              font-weight: 500;
            }
            
            .product-color {
              display: flex;
              align-items: center;
              gap: 10px;
            }
            
            .color-swatch {
              width: 24px;
              height: 24px;
              border-radius: 50%;
              border: 2px solid #e2e8f0;
              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
              display: inline-block;
              vertical-align: middle;
            }
            
            .color-name {
              font-size: 1rem;
              color: #64748b;
              font-weight: 500;
            }
            
            .table-container {
              overflow-x: auto;
              margin: 0;
            }
            
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 0;
            }
            
            th {
              background: #f1f5f9;
              color: #475569;
              font-weight: 600;
              text-transform: uppercase;
              font-size: 0.75rem;
              letter-spacing: 0.05em;
              padding: 15px 12px;
              text-align: center;
              border-bottom: 2px solid #e2e8f0;
            }
            
            td {
              padding: 15px 12px;
              border-bottom: 1px solid #f1f5f9;
              vertical-align: middle;
              text-align: center;
            }
            
            .product-info-cell {
              text-align: left;
            }
            
            .status-badge {
              display: inline-flex;
              align-items: center;
              padding: 6px 12px;
              border-radius: 20px;
              font-size: 0.75rem;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.05em;
            }
            
            .status-completed {
              background: #d1fae5;
              color: #065f46;
            }
            
            .status-pending {
              background: #fef3c7;
              color: #92400e;
            }
            
            .quantity-cell {
              font-weight: 600;
              font-size: 1.1rem;
              color: #1e293b;
            }
            
            .price-cell {
              font-weight: 600;
              color: #059669;
            }
            
            .total-cell {
              font-weight: 700;
              font-size: 1.2rem;
              color: #1e293b;
            }
            
            .footer {
              background: #f8fafc;
              padding: 25px;
              border-top: 1px solid #e2e8f0;
              text-align: center;
            }
            
            .print-button {
              background: #dc2626;
              color: white;
              border: none;
              padding: 12px 24px;
              border-radius: 8px;
              font-size: 1rem;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.2s;
            }
            
            .print-button:hover {
              background: #b91c1c;
              transform: translateY(-1px);
            }
            
            .delivery-info {
              background: #dbeafe;
              border: 1px solid #93c5fd;
              border-radius: 8px;
              padding: 20px;
              margin: 20px 0;
            }
            
            .delivery-info h3 {
              margin: 0 0 15px 0;
              color: #1e40af;
              font-size: 1.25rem;
              font-weight: 600;
            }
            
            .delivery-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: 15px;
            }
            
            .delivery-item {
              background: white;
              padding: 15px;
              border-radius: 6px;
              border: 1px solid #bfdbfe;
            }
            
            .delivery-item h4 {
              margin: 0 0 5px 0;
              color: #374151;
              text-transform: uppercase;
              letter-spacing: 0.05em;
              font-size: 0.875rem;
            }
            
            .delivery-item p {
              margin: 0;
              font-size: 1rem;
              font-weight: 600;
              color: #1e40af;
            }
            
            .size-header {
              min-width: 60px;
            }
            
            @media print {
              body { background: white; }
              .print-container { box-shadow: none; }
              .print-button { display: none; }
              .product-card { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            <div class="header">
              <h1>Salida de Almacén #${documentWithImages.number}</h1>
              <div class="header-info">
                <div class="header-item">
                  <h3>Cliente</h3>
                  <p>${documentWithImages.customer?.name || 'N/A'}</p>
                </div>
                <div class="header-item">
                  <h3>Fecha de Salida</h3>
                  <p>${new Date(documentWithImages.date).toLocaleDateString('es-ES', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</p>
                </div>
                <div class="header-item">
                  <h3>Total de Salida</h3>
                  <p>€${documentWithImages.total?.toFixed(2) || '0.00'}</p>
                </div>
                <div class="header-item">
                  <h3>Fecha de Impresión</h3>
                  <p>${new Date().toLocaleDateString('es-ES', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</p>
                </div>
              </div>
            </div>
            
            ${documentWithImages.delivery ? `
              <div class="delivery-info">
                <h3>📦 Información de Entrega</h3>
                <div class="delivery-grid">
                  <div class="delivery-item">
                    <h4>Número de Entrega</h4>
                    <p>${document.delivery.delivery_number}</p>
                  </div>
                  <div class="delivery-item">
                    <h4>Pedido Asociado</h4>
                    <p>#${document.delivery.order?.number || 'N/A'}</p>
                  </div>
                </div>
              </div>
            ` : ''}
            
            <div class="content">
              ${Object.values(groupedItems).map(item => `
                <div class="product-card">
                  <div class="product-header">
                    ${item.product?.images && item.product.images.length > 0 ? 
                      `<img src="${item.product.images[item.product.main_image_index || 0]}" alt="${item.description}" class="product-image">` : 
                      `<div class="product-image" style="background: #e2e8f0; display: flex; align-items: center; justify-content: center; color: #64748b; font-size: 0.875rem; text-align: center;">Sin imagen</div>`
                    }
                    <div class="product-info">
                      <h2 class="product-code">${item.product_code}</h2>
                      <p class="product-description">${item.description}</p>
                      <div class="product-color">
                        <div class="color-swatch" style="background-color: ${getColorHex(item.color)}; border: 2px solid #e2e8f0;" title="Color: ${getColorDescription(item.color)} (${getColorHex(item.color)})"></div>
                        <span class="color-name">${getColorDescription(item.color)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div class="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th class="product-info-cell">Estado</th>
                          <th class="product-info-cell">Producto</th>
                          <th class="product-info-cell">Color</th>
                          ${allSizes.map(size => `<th class="size-header">${size}</th>`).join('')}
                          <th>Total Unid.</th>
                          <th>Precio</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td class="product-info-cell">
                            <span class="status-badge ${documentWithImages.status === 'completed' ? 'status-completed' : 'status-pending'}">
                              ${documentWithImages.status === 'completed' ? 'Completado' : 'Pendiente'}
                            </span>
                          </td>
                          <td class="product-info-cell">
                            <strong>${item.product_code}</strong><br>
                            <span style="color: #64748b; font-size: 0.875rem;">${item.description}</span>
                          </td>
                          <td class="product-info-cell">
                            <div style="display: flex; align-items: center; gap: 8px;">
                              <div style="width: 20px; height: 20px; border-radius: 50%; background-color: ${getColorHex(item.color)}; border: 2px solid #e2e8f0;" title="Color: ${getColorDescription(item.color)} (${getColorHex(item.color)})"></div>
                              <span>${getColorDescription(item.color)}</span>
                            </div>
                          </td>
                          ${allSizes.map(size => `
                            <td class="quantity-cell">
                              ${item.sizes[size] || '-'}
                            </td>
                          `).join('')}
                          <td class="quantity-cell">${item.total_quantity}</td>
                          <td class="price-cell">€${item.price?.toFixed(2) || '0.00'}</td>
                          <td class="total-cell">€${item.total_amount.toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              `).join('') || '<p class="text-center text-gray-500 py-8">No hay artículos en este documento</p>'}
            </div>
            
            <div class="footer">
              <button class="print-button" onclick="window.print()">
                🖨️ Imprimir Salida de Almacén
              </button>
            </div>
          </div>
        </body>
      </html>
    `;
    
    try {
      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
    } catch (e) {
      console.error('Error al escribir en la ventana de impresión:', e);
      toast({ title: 'Error', description: 'Fallo al preparar el contenido de impresión', variant: 'destructive' });
    }
  };

  const handlePrintCompact = async (document) => {
    if (type !== 'salida') {
      toast({
        title: "Error",
        description: "Solo se pueden imprimir documentos de salida",
        variant: "destructive"
      });
      return;
    }

    const groupedByProduct = (document.items || []).reduce((acc, item) => {
      const key = item.product_id;
      if (!acc[key]) {
        acc[key] = {
          product_id: item.product_id,
          product_code: item.product?.code || 'N/A',
          description: item.product?.description || 'Sin descripción',
          total_quantity: 0,
          total_amount: 0
        };
      }
      acc[key].total_quantity += item.quantity || 0;
      acc[key].total_amount += (item.quantity || 0) * (item.price || 0);
      return acc;
    }, {});

    const compactItems = Object.values(groupedByProduct).sort((a, b) => {
      return (a.product_code || '').localeCompare(b.product_code || '');
    });

    const grandTotal = compactItems.reduce((sum, it) => sum + (it.total_amount || 0), 0);

    const printWindow = window.open('', '_blank');
    if (!printWindow || printWindow.closed) {
      toast({
        title: "Error",
        description: "No se pudo abrir la ventana de impresión (posible bloqueador de pop‑ups)",
        variant: "destructive"
      });
      return;
    }
    const printContent = `
      <html>
        <head>
          <title>Salida Compacta #${document.number}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Inter', Segoe UI, Roboto, Helvetica, Arial, sans-serif; font-size: 11px; margin: 12px; color: #111827; }
            .header { margin-bottom: 10px; }
            .title { font-size: 16px; font-weight: 700; margin: 0 0 6px 0; }
            .meta { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 6px; margin-bottom: 8px; }
            .meta-item { padding: 6px; border: 1px solid #e5e7eb; border-radius: 4px; }
            .label { font-size: 10px; color: #6b7280; text-transform: uppercase; }
            .value { font-size: 12px; font-weight: 600; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #e5e7eb; padding: 6px 8px; }
            th { background: #f9fafb; font-weight: 700; text-align: left; }
            td.qty, td.amount { text-align: right; }
            tfoot td { font-weight: 700; }
            .print-btn { margin-top: 10px; padding: 6px 10px; font-size: 12px; background: #111827; color: white; border: none; border-radius: 4px; }
            @media print { .print-btn { display: none; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1 class="title">Albarán de Salida (Compacto) #${document.number}</h1>
            <div class="meta">
              <div class="meta-item"><div class="label">Cliente</div><div class="value">${document.customer?.name || 'N/A'}</div></div>
              <div class="meta-item"><div class="label">Fecha</div><div class="value">${new Date(document.date).toLocaleDateString('es-ES')}</div></div>
              <div class="meta-item"><div class="label">Total documento</div><div class="value">€${(document.total || 0).toFixed(2)}</div></div>
              ${document.delivery ? `<div class="meta-item"><div class="label">Entrega</div><div class="value">${document.delivery.delivery_number} (Pedido #${document.delivery.order?.number || 'N/A'})</div></div>` : ''}
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th style="width: 18%">Código</th>
                <th>Descripción</th>
                <th style="width: 12%; text-align:right">Cantidad</th>
                <th style="width: 16%; text-align:right">Importe</th>
              </tr>
            </thead>
            <tbody>
              ${compactItems.length > 0 ? compactItems.map(it => `
                <tr>
                  <td>${it.product_code}</td>
                  <td>${it.description}</td>
                  <td class="qty">${it.total_quantity}</td>
                  <td class="amount">€${it.total_amount.toFixed(2)}</td>
                </tr>
              `).join('') : `<tr><td colspan="4" style="text-align:center; color:#6b7280;">Sin líneas</td></tr>`}
            </tbody>
            <tfoot>
              <tr>
                <td colspan="3" style="text-align:right">Total albarán</td>
                <td class="amount">€${grandTotal.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
          <button class="print-btn" onclick="window.print()">Imprimir</button>
        </body>
      </html>
    `;

    try {
      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
    } catch (e) {
      console.error('Error al escribir en la ventana de impresión (compacta):', e);
      toast({ title: 'Error', description: 'Fallo al preparar el contenido de impresión', variant: 'destructive' });
    }
  };

  const handleNew = () => {
    if (type === 'salida' && !isAdmin) {
      toast({
        title: "Error",
        description: "No tienes permisos para crear documentos de salida",
        variant: "destructive"
      });
      return;
    }
    
    window.location.hash = `#/${type}/new`;
  };

  const handleProductFilter = (productId) => {
    setSelectedProduct(productId);
  };

  const clearFilter = () => {
    setSelectedProduct(null);
  };

  const clearCustomerFilter = () => {
    setCustomerSearchTerm("");
  };

  const clearDateRangeFilter = () => {
    setDateRange({ from: "", to: "" });
  };

  const applyDateSeasonPreset = (presetId) => {
    if (presetId === "__none__") {
      clearDateRangeFilter();
      return;
    }
    const preset = seasonDatePresets.find((p) => p.id === presetId);
    if (!preset) return;
    setDateRange({ from: preset.from, to: preset.to });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">
          {type === 'entrada' ? 'Entradas artículos almacén' : 'Albaranes Salidas Almacén'}
        </h2>
        <Button 
          onClick={handleNew}
          disabled={type === 'salida' && !isAdmin}
        >
          <Plus className="mr-2 h-4 w-4" />
          Nueva {type === 'entrada' ? 'Entrada' : 'Salida'}
        </Button>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-gray-500" />
            <span className="font-medium">Filtros</span>
            {selectedProduct && (
              <span className="text-sm text-gray-500">
                ({filteredDocuments.length} documento{filteredDocuments.length !== 1 ? 's' : ''} encontrado{filteredDocuments.length !== 1 ? 's' : ''})
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsFilterExpanded(!isFilterExpanded)}
            className="flex items-center gap-1"
          >
            {isFilterExpanded ? (
              <>
                <ChevronUp className="h-4 w-4" />
                Ocultar
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4" />
                Mostrar
              </>
            )}
          </Button>
        </div>

        {isFilterExpanded && (
          <div className="space-y-4">
            {/* Filtro por producto */}
            <div className="space-y-3">
              <ProductSelector
                onSelect={handleProductFilter}
                value={selectedProduct}
              />
              {selectedProduct && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearFilter}
                    className="flex items-center gap-2"
                  >
                    <X className="h-4 w-4" />
                    Limpiar filtro de producto
                  </Button>
                  <span className="text-sm text-gray-600">
                    Mostrando solo documentos que contienen el producto seleccionado
                  </span>
                </div>
              )}
            </div>

            {/* Filtro por cliente (solo salidas) */}
            {type === 'salida' && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Filtrar por cliente</label>
                <div className="flex items-center gap-2">
                  <Input
                    value={customerSearchTerm}
                    onChange={(e) => setCustomerSearchTerm(e.target.value)}
                    placeholder="Nombre del cliente..."
                    className="max-w-sm"
                  />
                  {customerSearchTerm && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearCustomerFilter}
                      className="flex items-center gap-2"
                    >
                      <X className="h-4 w-4" />
                      Limpiar filtro de cliente
                    </Button>
                  )}
                </div>
                {customerSearchTerm && (
                  <span className="text-sm text-gray-600">
                    {filteredDocuments.length} documento{filteredDocuments.length !== 1 ? 's' : ''} tras filtrar por cliente
                  </span>
                )}
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">Rango de fechas</label>
              <Select onValueChange={applyDateSeasonPreset}>
                <SelectTrigger className="max-w-md">
                  <SelectValue placeholder="Temporada (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Todos / sin temporada</SelectItem>
                  {seasonDatePresets.map((preset) => (
                    <SelectItem key={`doc-date-${preset.id}`} value={preset.id}>
                      {preset.name} ({preset.from} - {preset.to})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, from: e.target.value }))}
                  className="max-w-xs"
                />
                <Input
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => setDateRange((prev) => ({ ...prev, to: e.target.value }))}
                  className="max-w-xs"
                />
                {(dateRange.from || dateRange.to) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearDateRangeFilter}
                    className="flex items-center gap-2"
                  >
                    <X className="h-4 w-4" />
                    Limpiar fechas
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Número</TableHead>
              <TableHead>Fecha</TableHead>
              {type === 'salida' && <TableHead>Cliente</TableHead>}
              <TableHead className="text-right" title="Total de unidades del albarán; en regularizaciones se conserva el signo">Prendas</TableHead>
              <TableHead>Total</TableHead>
              {type === 'entrada' && <TableHead>Modo</TableHead>}
              {type === 'salida' && <TableHead>Entrega</TableHead>}
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredDocuments.map((doc) => (
              <TableRow key={doc.id}>
                <TableCell>{doc.number}</TableCell>
                <TableCell>{formatDate(doc.date)}</TableCell>
                {type === 'salida' && <TableCell>{doc.customer?.name || '-'}</TableCell>}
                <TableCell className="text-right tabular-nums">{doc.totalQuantity.toLocaleString('es-ES')}</TableCell>
                <TableCell>{formatCurrency(doc.total)}</TableCell>
                {type === 'entrada' && (
                  <TableCell>
                    {(() => {
                      const entryMode = doc.entry_mode || 'normal';

                      if (entryMode === 'count') {
                        return (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-300" title={doc.regularization_reason || 'Recuento'}>
                            Recuento
                          </span>
                        );
                      }

                      if (entryMode === 'adjustment') {
                        return (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-300" title={doc.regularization_reason || 'Ajuste manual'}>
                            Ajuste manual
                          </span>
                        );
                      }

                      return (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-300">
                          Normal
                        </span>
                      );
                    })()}
                  </TableCell>
                )}
                {type === 'salida' && (
                  <TableCell>
                    {doc.delivery ? (
                      <div className="text-xs">
                        <div className="font-medium text-blue-600">
                          {doc.delivery.delivery_number}
                        </div>
                        <div className="text-gray-500">
                          Pedido #{doc.delivery.order?.number || 'N/A'}
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">Sin entrega</span>
                    )}
                  </TableCell>
                )}
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {type === 'salida' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handlePrint(doc)}
                        title="Imprimir salida de almacén"
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <Printer className="h-4 w-4" />
                      </Button>
                    )}
                    {type === 'salida' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handlePrintCompact(doc)}
                        title="Imprimir compacto"
                        className="text-gray-600 hover:text-gray-700"
                      >
                        <Printer className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(doc)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(doc)}
                      title={type === 'salida' ? 
                        (doc.delivery ? 
                          'Eliminar documento y recuperar cantidades entregadas' : 
                          'Eliminar documento de salida'
                        ) : 
                        'Eliminar documento'
                      }
                      className={type === 'salida' && doc.delivery ? 'text-orange-600 hover:text-blue-700' : ''}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              {type === 'salida' ? (
                <>
                  <strong>⚠️ ATENCIÓN: Documento de Salida</strong>
                  <br /><br />
                  Esta acción eliminará el documento de salida y realizará las siguientes operaciones:
                  <br /><br />
                  ✅ <strong>Recuperará las cantidades al inventario</strong>
                  ✅ <strong>Revertirá las cantidades entregadas en el pedido</strong>
                  ✅ <strong>Eliminará los registros de entrega asociados</strong>
                  ✅ <strong>Actualizará el estado del pedido</strong>
                  <br /><br />
                  <span className="text-orange-600 font-medium">
                    Esta operación es reversible y permite rehacer la entrega correctamente.
                  </span>
                </>
              ) : (
                <>
                  Esta acción no se puede deshacer. Se eliminará permanentemente el documento
                  y se actualizará el inventario.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className={type === 'salida' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {type === 'salida' ? 'Recuperar y Eliminar' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
